/**
 * Utility functions for generating default city options
 */

import { getPopularCitiesInState } from "./cityStateMap"
import {
  getRecentSearchesForField,
  saveRecentSearch,
} from "./recentSearches"

// Top popular cities across India (general defaults)
const POPULAR_CITIES = [
  "Mumbai",
  "Delhi",
  "Bangalore",
  "Hyderabad",
  "Ahmedabad",
  "Chennai",
  "Kolkata",
  "Pune",
  "Jaipur",
  "Surat",
]

/**
 * Convert city name to place option format
 * @param {string} cityName - Name of the city
 * @returns {Promise<Object>} Place option object
 */
export const cityNameToPlaceOption = async (cityName) => {
  if (!cityName) return null

  // Format city name with ", India" for better Google Places matching
  const formattedCity = cityName.includes(",") ? cityName : `${cityName}, India`

  return {
    label: cityName,
    value: {
      description: formattedCity,
      place_id: null,
    },
  }
}

/**
 * Get default options for a field (from/to)
 * @param {string} field - 'from' or 'to'
 * @param {string} currentCity - Current city name (for same-state popular cities)
 * @param {string} fromCity - Selected "from" city (for "to" field to show popular destinations)
 * @returns {Promise<Array>} Array of default options
 */
export const getDefaultOptions = async (field, currentCity = null, fromCity = null) => {
  const options = []

  // For "to" field, prioritize popular cities from the same state as "from" city
  if (field === "to" && fromCity) {
    try {
      // Get popular cities in same state as "from" city (exclude the "from" city itself)
      const popularCitiesInState = getPopularCitiesInState(
        fromCity,
        fromCity, // Exclude the "from" city from results
        10 // Get more cities for destinations
      )
      const stateCityOptions = await Promise.all(
        popularCitiesInState
          .slice(0, 10)
          .map((city) => cityNameToPlaceOption(city))
      )
      // Filter out nulls and avoid duplicates
      const filteredStateCities = stateCityOptions
        .filter((opt) => opt && !options.some((o) => o.label === opt.label))
        .slice(0, 10)
      options.push(...filteredStateCities)
    } catch (error) {
      console.error("Error getting popular cities in state:", error)
    }
  }

  // 1. Get recent searches (2-5)
  const recentSearches = getRecentSearchesForField(field)
  if (recentSearches.length > 0) {
    // Avoid duplicates with state cities
    const filteredRecent = recentSearches
      .filter((search) => !options.some((o) => o.label === search.label))
      .slice(0, 5)
    options.push(...filteredRecent)
  }

  // 2. Get popular cities in same state (for "from" field or when no "from" is selected)
  if (currentCity && field !== "to") {
    try {
      const popularCitiesInState = getPopularCitiesInState(
        currentCity,
        null,
        5
      )
      const stateCityOptions = await Promise.all(
        popularCitiesInState
          .slice(0, 5)
          .map((city) => cityNameToPlaceOption(city))
      )
      // Filter out nulls and avoid duplicates
      const filteredStateCities = stateCityOptions
        .filter((opt) => opt && !options.some((o) => o.label === opt.label))
        .slice(0, 5)
      options.push(...filteredStateCities)
    } catch (error) {
      console.error("Error getting popular cities in state:", error)
    }
  }

  // 3. Add general popular cities (5-10, avoiding duplicates) - only if we don't have enough options
  if (options.length < 10) {
    const generalCityOptions = await Promise.all(
      POPULAR_CITIES.map((city) => cityNameToPlaceOption(city))
    )
    const filteredGeneralCities = generalCityOptions
      .filter((opt) => opt && !options.some((o) => o.label === opt.label))
      .slice(0, 10 - options.length)

    options.push(...filteredGeneralCities)
  }

  // Return combined options (max 15 total)
  return options.slice(0, 15)
}

